import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL, ListingApiService } from '@cnt-workspace/data-access';
import { IDtoListingMedia, IListing, IListingWhere } from '@curbnturf/entities';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, toArray } from 'rxjs/operators';

const API_URL = BASE_API_URL + 'listing';

/** The server refuses larger pages ("Page size is greater than the maximum allowed", site-find.service.ts). */
export const LISTING_MAX_PAGE_SIZE = 100;

export interface IAdminListingPage {
  total: number;
  page: number;
  listings: IListing[];
}

/**
 * The listing calls admin needs that cnt-landing's ListingApiService leaves
 * out. Everything else (section updates, enable/disable, images, sites) is
 * called on ListingApiService directly — the server's ACL lets admins act on
 * any owner's listing (acl.service verifyListingOwner → checkIfAdmin).
 */
@Injectable({ providedIn: 'root' })
export class AdminListingApiService {
  private readonly http = inject(HttpClient);
  private readonly listings = inject(ListingApiService);

  list(where: IListingWhere, page: number, pageSize: number, sort = 'id'): Observable<IAdminListingPage> {
    return this.listings.fetchAll(where, sort, page, pageSize);
  }

  /**
   * Every listing matching `where`. Page 1 tells us the total; the remaining
   * pages are fetched four at a time and put back in order. Page numbers are
   * tracked here rather than read back from the response: the server echoes
   * the query value, which can arrive as a string.
   */
  listAll(where: IListingWhere, pageSize = LISTING_MAX_PAGE_SIZE): Observable<IListing[]> {
    return this.list(where, 1, pageSize).pipe(
      switchMap((first) => {
        const pages = Math.ceil(Number(first.total) / pageSize);
        if (pages <= 1 || !first.listings.length) return of(first.listings);
        const rest = Array.from({ length: pages - 1 }, (_, i) => i + 2);
        return from(rest).pipe(
          mergeMap((page) => this.list(where, page, pageSize).pipe(map((res) => ({ page, res }))), 4),
          toArray(),
          map((more) => [
            ...first.listings,
            ...more.sort((a, b) => a.page - b.page).flatMap(({ res }) => res.listings),
          ]),
        );
      }),
    );
  }

  restore(listingId: number): Observable<IListing> {
    return this.http.post<IListing>(`${API_URL}/${listingId}/restore`, null);
  }

  /** `PUT /listing/:id/featured` — behind the server's AdminAuthGuard. */
  setFeatured(listingId: number, featured: boolean): Observable<IListing> {
    return this.http.put<IListing>(`${API_URL}/${listingId}/featured`, { featured });
  }

  /** `PUT /listing/:id/media` — youtubeCode + rvcastUrl. */
  updateMedia(listingId: number, media: IDtoListingMedia): Observable<IListing> {
    return this.http.put<IListing>(`${API_URL}/${listingId}/media`, media);
  }
}
